// Book: "La Liberación y El Reino de Dios" / "Deliverance and the Kingdom of God"
// Author/Attribution: FaithCycle
// Bilingual (es/en). BookReader picks the active language from AppContext.
// Block shape:
//   { type: "p" | "h3" | "quote" | "note" | "highlight",
//     text: { es, en },
//     ref?: { es, en } }        // ref is optional (only on quote)

const BOOK = {
  id: "liberacion-reino-dios",
  title: { es: "La Liberación y El Reino de Dios", en: "Deliverance and the Kingdom of God" },
  subtitle: {
    es: "Estudio bíblico sobre el gobierno de Dios en el creyente",
    en: "Bible study on God's rule in the believer",
  },
  attribution: "FaithCycle",
  category: "power",
  chapters: [
    // ---------- Introducción ----------
    {
      id: "introduccion",
      label: { es: "Introducción", en: "Introduction" },
      title: { es: "Introducción", en: "Introduction" },
      blocks: [
        {
          type: "p",
          text: {
            es: "El Reino de Dios no es solamente una idea espiritual, sino el gobierno de Dios manifestado en la vida del creyente. La liberación no es un fin en sí mismo; su verdadero propósito es que Cristo gobierne donde antes había opresión, desorden, tinieblas o vacío.",
            en: "The Kingdom of God is not only a spiritual idea; it is the rule of God made manifest in the life of the believer. Deliverance is not an end in itself; its true purpose is that Christ may reign where before there was oppression, disorder, darkness or emptiness.",
          },
        },
        {
          type: "p",
          text: { es: "Romanos 14:17 declara:", en: "Romans 14:17 declares:" },
        },
        {
          type: "quote",
          text: {
            es: "Porque el reino de Dios no es comida ni bebida, sino justicia, paz y gozo en el Espíritu Santo.",
            en: "For the kingdom of God is not meat and drink; but righteousness, and peace, and joy in the Holy Ghost.",
          },
          ref: { es: "Romanos 14:17", en: "Romans 14:17" },
        },
        {
          type: "p",
          text: {
            es: "Esto nos muestra que el Reino no consiste principalmente en cosas externas, rituales o discusiones secundarias, sino en una transformación interna producida por el Espíritu Santo.",
            en: "This shows us that the Kingdom does not consist mainly of outward things, rituals or secondary debates, but of an inner transformation produced by the Holy Spirit.",
          },
        },
      ],
    },

    // ---------- Capítulo 1 ----------
    {
      id: "capitulo-1",
      label: { es: "Capítulo 1", en: "Chapter 1" },
      title: { es: "¿Qué es el Reino de Dios?", en: "What is the Kingdom of God?" },
      blocks: [
        {
          type: "p",
          text: {
            es: "El Reino de Dios es el gobierno de Dios establecido en nosotros. Donde el Reino gobierna, hay justicia, paz y gozo en el Espíritu Santo.",
            en: "The Kingdom of God is God's rule established within us. Where the Kingdom reigns, there is righteousness, peace and joy in the Holy Spirit.",
          },
        },
        { type: "h3", text: { es: "Tres evidencias del Reino de Dios", en: "Three evidences of the Kingdom of God" } },
        { type: "h3", text: { es: "1. Justicia — dikaiosýnē", en: "1. Righteousness — dikaiosýnē" } },
        {
          type: "p",
          text: {
            es: "La justicia es vivir en rectitud, alineados con la voluntad de Dios y en una relación correcta con Él.",
            en: "Righteousness is living uprightly, aligned with the will of God and in a right relationship with Him.",
          },
        },
        { type: "h3", text: { es: "2. Paz — eirḗnē", en: "2. Peace — eirḗnē" } },
        {
          type: "p",
          text: {
            es: "La paz es una paz integral, interior, estabilidad y reconciliación con Dios.",
            en: "Peace is a whole peace — inner peace, stability, and reconciliation with God.",
          },
        },
        { type: "h3", text: { es: "3. Gozo — chará", en: "3. Joy — chará" } },
        {
          type: "p",
          text: {
            es: "El gozo es una alegría profunda que no depende de las circunstancias.",
            en: "Joy is a deep gladness that does not depend on circumstances.",
          },
        },
        {
          type: "highlight",
          text: {
            es: "Frase para recordar: La liberación no es un fin en sí mismo; el objetivo es establecer el Reino de Dios en nosotros. Y donde el Reino gobierna, hay justicia, paz y gozo en el Espíritu Santo.",
            en: "Key thought: Deliverance is not an end in itself; the goal is to establish the Kingdom of God within us. And where the Kingdom reigns, there is righteousness, peace and joy in the Holy Spirit.",
          },
        },
      ],
    },

    // ---------- Capítulo 2 ----------
    {
      id: "capitulo-2",
      label: { es: "Capítulo 2", en: "Chapter 2" },
      title: { es: "Justicia: vivir alineados con Dios", en: "Righteousness: living aligned with God" },
      blocks: [
        {
          type: "p",
          text: {
            es: "La palabra justicia en griego es δικαιοσύνη — dikaiosýnē. Significa rectitud, integridad y aquello que está conforme a lo que Dios considera correcto.",
            en: "The Greek word for righteousness is δικαιοσύνη — dikaiosýnē. It means uprightness, integrity, and that which conforms to what God considers right.",
          },
        },
        {
          type: "p",
          text: {
            es: "La justicia bíblica tiene dos dimensiones principales.",
            en: "Biblical righteousness has two main dimensions.",
          },
        },
        { type: "h3", text: { es: "Una relación correcta con Dios", en: "A right relationship with God" } },
        {
          type: "p",
          text: {
            es: "La justicia comienza porque Dios nos reconcilia consigo mismo por medio de Cristo.",
            en: "Righteousness begins because God reconciles us to Himself through Christ.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Justificados, pues, por la fe, tenemos paz para con Dios por medio de nuestro Señor Jesucristo.",
            en: "Therefore being justified by faith, we have peace with God through our Lord Jesus Christ.",
          },
          ref: { es: "Romanos 5:1", en: "Romans 5:1" },
        },
        {
          type: "p",
          text: {
            es: "Antes estábamos separados de Dios, pero por la obra de Cristo fuimos reconciliados con Él.",
            en: "Before, we were separated from God, but through the work of Christ we were reconciled to Him.",
          },
        },
        {
          type: "note",
          text: {
            es: "La justicia no empieza con mi conducta; empieza con mi relación con Dios.",
            en: "Righteousness does not start with my behavior; it starts with my relationship with God.",
          },
        },
        { type: "h3", text: { es: "2. Vivir alineados con la voluntad de Dios", en: "2. Living aligned with God's will" } },
        {
          type: "p",
          text: {
            es: "Una vez reconciliados, comenzamos a vivir conforme a lo que Dios quiere.",
            en: "Once reconciled, we begin to live according to what God desires.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Mas buscad primeramente el reino de Dios y su justicia, y todas estas cosas os serán añadidas.",
            en: "But seek ye first the kingdom of God, and his righteousness; and all these things shall be added unto you.",
          },
          ref: { es: "Mateo 6:33", en: "Matthew 6:33" },
        },
        {
          type: "p",
          text: {
            es: "Buscar su justicia significa preguntarnos: ¿Qué piensa Dios? ¿Qué agrada a Dios? ¿Qué decisión honra a Dios?",
            en: "To seek His righteousness means to ask: What does God think? What pleases God? What decision honors God?",
          },
        },
        {
          type: "note",
          text: {
            es: "La justicia es permitir que la voluntad de Dios gobierne mis decisiones.",
            en: "Righteousness is letting God's will govern my decisions.",
          },
        },
        { type: "h3", text: { es: "3. La justicia produce un cambio de conducta", en: "3. Righteousness produces a change in conduct" } },
        {
          type: "p",
          text: {
            es: "La justicia no es solamente una declaración espiritual; también produce fruto visible.",
            en: "Righteousness is not merely a spiritual declaration; it also produces visible fruit.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Hijitos, nadie os engañe; el que hace justicia es justo, como él es justo.",
            en: "Little children, let no man deceive you: he that doeth righteousness is righteous, even as he is righteous.",
          },
          ref: { es: "1 Juan 3:7", en: "1 John 3:7" },
        },
        {
          type: "note",
          text: {
            es: "Lo que Dios hizo en mi interior comienza a manifestarse en mi exterior.",
            en: "What God has done inside me begins to be shown on the outside.",
          },
        },
        { type: "h3", text: { es: "4. La justicia es un camino", en: "4. Righteousness is a way of life" } },
        {
          type: "p",
          text: {
            es: "No es un acto aislado, sino una forma diaria de vivir.",
            en: "It is not an isolated act, but a daily way of living.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Reconócelo en todos tus caminos, y él enderezará tus veredas.",
            en: "In all thy ways acknowledge him, and he shall direct thy paths.",
          },
          ref: { es: "Proverbios 3:6", en: "Proverbs 3:6" },
        },
        { type: "h3", text: { es: "5. La justicia es evidencia del Reino", en: "5. Righteousness is evidence of the Kingdom" } },
        {
          type: "p",
          text: {
            es: "Cuando el Reino gobierna nuestra vida, dejamos de hacer solamente nuestra voluntad. Donde el Reino gobierna hay decisiones correctas, obediencia, integridad y una relación sana con Dios.",
            en: "When the Kingdom rules our life, we stop doing only our own will. Where the Kingdom reigns there are right decisions, obedience, integrity and a healthy relationship with God.",
          },
        },
        {
          type: "highlight",
          text: {
            es: "Frase para recordar: La justicia es vivir en una relación correcta con Dios y permitir que esa relación transforme la manera en que vivo, pienso y decido cada día.",
            en: "Key thought: Righteousness is living in a right relationship with God and allowing that relationship to transform the way I live, think and decide each day.",
          },
        },
      ],
    },

    // ---------- Capítulo 3 ----------
    {
      id: "capitulo-3",
      label: { es: "Capítulo 3", en: "Chapter 3" },
      title: { es: "Justicia, paz y gozo en la cruz", en: "Righteousness, peace and joy at the cross" },
      blocks: [
        {
          type: "p",
          text: {
            es: "Un ejemplo práctico de Romanos 14:17 se encuentra en los dos ladrones crucificados junto a Jesús.",
            en: "A practical example of Romans 14:17 is found in the two thieves crucified next to Jesus.",
          },
        },
        { type: "p", text: { es: "Texto base: Lucas 23:39-43.", en: "Text: Luke 23:39-43." } },
        {
          type: "p",
          text: {
            es: "Uno de ellos miraba a Jesús solamente como alguien que debía resolverle el problema inmediato:",
            en: "One of them looked at Jesus only as someone who should fix his immediate problem:",
          },
        },
        {
          type: "quote",
          text: {
            es: "Si tú eres el Cristo, sálvate a ti mismo y a nosotros.",
            en: "If thou be Christ, save thyself and us.",
          },
        },
        {
          type: "p",
          text: {
            es: "Su enfoque era escapar de las consecuencias sin reconocer su pecado.",
            en: "His focus was to escape the consequences without recognizing his sin.",
          },
        },
        { type: "p", text: { es: "Pero el otro ladrón respondió:", en: "But the other thief answered:" } },
        {
          type: "quote",
          text: {
            es: "¿Ni aun temes tú a Dios, estando en la misma condenación? Nosotros, a la verdad, justamente padecemos, porque recibimos lo que merecieron nuestros hechos; mas éste ningún mal hizo.",
            en: "Dost not thou fear God, seeing thou art in the same condemnation? And we indeed justly; for we receive the due reward of our deeds: but this man hath done nothing amiss.",
          },
          ref: { es: "Lucas 23:40-41", en: "Luke 23:40-41" },
        },
        { type: "h3", text: { es: "Aparece la justicia", en: "Righteousness appears" } },
        { type: "p", text: { es: "El ladrón arrepentido reconoció dos verdades.", en: "The repentant thief acknowledged two truths." } },
        {
          type: "p",
          text: {
            es: "Primero, reconoció su condición pecadora. Dejó de verse como víctima y aceptó su responsabilidad.",
            en: "First, he acknowledged his sinful condition. He stopped seeing himself as a victim and accepted his responsibility.",
          },
        },
        { type: "p", text: { es: "Segundo, reconoció la inocencia de Cristo:", en: "Second, he acknowledged the innocence of Christ:" } },
        {
          type: "quote",
          text: { es: "Éste ningún mal hizo.", en: "This man hath done nothing amiss." },
        },
        {
          type: "p",
          text: {
            es: "Jesús no merecía la cruz. Nosotros sí. Sin embargo, Jesús ocupó nuestro lugar.",
            en: "Jesus did not deserve the cross. We did. Yet Jesus took our place.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Mas él herido fue por nuestras rebeliones, molido por nuestros pecados; el castigo de nuestra paz fue sobre él, y por su llaga fuimos nosotros curados.",
            en: "But he was wounded for our transgressions, he was bruised for our iniquities: the chastisement of our peace was upon him; and with his stripes we are healed.",
          },
          ref: { es: "Isaías 53:5", en: "Isaiah 53:5" },
        },
        { type: "h3", text: { es: "La justicia produce paz", en: "Righteousness produces peace" } },
        {
          type: "p",
          text: {
            es: "Cuando entendemos que Cristo tomó nuestro lugar, dejamos de vivir como condenados.",
            en: "When we understand that Christ took our place, we stop living as those condemned.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Justificados, pues, por la fe, tenemos paz para con Dios por medio de nuestro Señor Jesucristo.",
            en: "Therefore being justified by faith, we have peace with God through our Lord Jesus Christ.",
          },
          ref: { es: "Romanos 5:1", en: "Romans 5:1" },
        },
        {
          type: "p",
          text: {
            es: "La paz nace cuando sé que la deuda fue pagada. Ya no vivo huyendo de Dios. Ya no vivo esperando castigo. Ahora tengo paz porque Cristo tomó mi lugar.",
            en: "Peace is born when I know the debt has been paid. I no longer live running from God. I no longer live expecting punishment. Now I have peace because Christ took my place.",
          },
        },
        { type: "h3", text: { es: "La paz produce gozo", en: "Peace produces joy" } },
        {
          type: "p",
          text: {
            es: "Después de reconocer su pecado y creer en Jesús, el ladrón recibió una promesa:",
            en: "After acknowledging his sin and believing in Jesus, the thief received a promise:",
          },
        },
        {
          type: "quote",
          text: {
            es: "De cierto te digo que hoy estarás conmigo en el paraíso.",
            en: "Verily I say unto thee, To day shalt thou be with me in paradise.",
          },
          ref: { es: "Lucas 23:43", en: "Luke 23:43" },
        },
        {
          type: "p",
          text: {
            es: "La palabra paraíso hace referencia al jardín, al Edén, a la restauración de la comunión con Dios y a la entrada al Reino.",
            en: "The word paradise refers to the garden, to Eden, to the restoration of fellowship with God, and to entering the Kingdom.",
          },
        },
        {
          type: "p",
          text: {
            es: "La secuencia espiritual es: Justicia → Paz → Gozo.",
            en: "The spiritual sequence is: Righteousness → Peace → Joy.",
          },
        },
        { type: "p", text: { es: "David oró:", en: "David prayed:" } },
        {
          type: "quote",
          text: {
            es: "Vuélveme el gozo de tu salvación, y espíritu noble me sustente.",
            en: "Restore unto me the joy of thy salvation; and uphold me with thy free spirit.",
          },
          ref: { es: "Salmo 51:12", en: "Psalm 51:12" },
        },
        {
          type: "p",
          text: {
            es: "Aun en medio de las pruebas hay gozo. El ladrón seguía en la cruz. Sus circunstancias no cambiaron de inmediato, pero su destino eterno sí cambió.",
            en: "Even in the midst of trials there is joy. The thief was still on the cross. His circumstances did not immediately change, but his eternal destiny did.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Porque tengo por cierto que las aflicciones del tiempo presente no son comparables con la gloria venidera que en nosotros ha de manifestarse.",
            en: "For I reckon that the sufferings of this present time are not worthy to be compared with the glory which shall be revealed in us.",
          },
          ref: { es: "Romanos 8:18", en: "Romans 8:18" },
        },
        {
          type: "note",
          text: {
            es: "Nota final: El Reino de Dios comienza cuando reconozco mi pecado, acepto que Cristo tomó mi lugar, recibo la justicia de Dios por la fe, obtengo paz con Dios y entonces el gozo de la salvación comienza a gobernar mi vida.",
            en: "Final note: The Kingdom of God begins when I acknowledge my sin, accept that Christ took my place, receive God's righteousness by faith, obtain peace with God, and then the joy of salvation begins to rule my life.",
          },
        },
      ],
    },

    // ---------- Capítulo 4 ----------
    {
      id: "capitulo-4",
      label: { es: "Capítulo 4", en: "Chapter 4" },
      title: { es: "El Edén como figura del Reino de Dios", en: "Eden as a figure of the Kingdom of God" },
      blocks: [
        {
          type: "p",
          text: {
            es: "La palabra Edén proviene del hebreo עֵדֶן — ʿeden y significa deleite, placer, delicia y abundancia.",
            en: "The word Eden comes from Hebrew עֵדֶן — ʿeden, and means delight, pleasure, delicacy and abundance.",
          },
        },
        {
          type: "p",
          text: {
            es: "El Edén era un lugar de deleite porque existía un equilibrio perfecto entre Dios, el hombre y la creación.",
            en: "Eden was a place of delight because there was a perfect balance between God, man and creation.",
          },
        },
        { type: "h3", text: { es: "¿Qué había en el Edén?", en: "What was in Eden?" } },
        { type: "p", text: { es: "Había agua.", en: "There was water." } },
        {
          type: "quote",
          text: {
            es: "Y salía de Edén un río para regar el huerto, y de allí se repartía en cuatro brazos.",
            en: "And a river went out of Eden to water the garden; and from thence it was parted, and became into four heads.",
          },
          ref: { es: "Génesis 2:10", en: "Genesis 2:10" },
        },
        {
          type: "p",
          text: {
            es: "El agua representa vida, provisión y la presencia de Dios.",
            en: "Water represents life, provision and the presence of God.",
          },
        },
        { type: "p", text: { es: "Había oro.", en: "There was gold." } },
        {
          type: "quote",
          text: { es: "El oro de aquella tierra es bueno.", en: "And the gold of that land is good." },
          ref: { es: "Génesis 2:12", en: "Genesis 2:12" },
        },
        {
          type: "p",
          text: {
            es: "Dios creó un ambiente de abundancia y provisión.",
            en: "God created an environment of abundance and provision.",
          },
        },
        { type: "p", text: { es: "Había plantas y alimento.", en: "There were plants and food." } },
        {
          type: "quote",
          text: {
            es: "Jehová Dios hizo nacer de la tierra todo árbol delicioso a la vista, y bueno para comer.",
            en: "And out of the ground made the LORD God to grow every tree that is pleasant to the sight, and good for food.",
          },
          ref: { es: "Génesis 2:9", en: "Genesis 2:9" },
        },
        { type: "p", text: { es: "No había escasez.", en: "There was no scarcity." } },
        { type: "p", text: { es: "Había animales.", en: "There were animals." } },
        {
          type: "quote",
          text: {
            es: "Jehová Dios formó, pues, de la tierra toda bestia del campo y toda ave de los cielos…",
            en: "And out of the ground the LORD God formed every beast of the field, and every fowl of the air…",
          },
          ref: { es: "Génesis 2:19", en: "Genesis 2:19" },
        },
        {
          type: "p",
          text: {
            es: "La creación estaba bajo el gobierno del hombre.",
            en: "Creation was under man's rule.",
          },
        },
        { type: "p", text: { es: "Había presencia de Dios.", en: "God's presence was there." } },
        {
          type: "quote",
          text: {
            es: "Y oyeron la voz de Jehová Dios que se paseaba en el huerto…",
            en: "And they heard the voice of the LORD God walking in the garden…",
          },
          ref: { es: "Génesis 3:8", en: "Genesis 3:8" },
        },
        {
          type: "p",
          text: {
            es: "El Reino de Dios es, ante todo, la presencia de Dios habitando con el hombre.",
            en: "The Kingdom of God is, above all, the presence of God dwelling with man.",
          },
        },
        { type: "p", text: { es: "Había propósito.", en: "There was purpose." } },
        {
          type: "quote",
          text: {
            es: "Tomó, pues, Jehová Dios al hombre y lo puso en el huerto de Edén, para que lo labrara y lo guardase.",
            en: "And the LORD God took the man, and put him into the garden of Eden to dress it and to keep it.",
          },
          ref: { es: "Génesis 2:15", en: "Genesis 2:15" },
        },
        {
          type: "p",
          text: {
            es: "Adán tenía una asignación. Su propósito no era sobrevivir, sino extender el Reino de Dios.",
            en: "Adam had an assignment. His purpose was not to survive, but to extend the Kingdom of God.",
          },
        },
        { type: "p", text: { es: "Había familia.", en: "There was family." } },
        {
          type: "quote",
          text: {
            es: "No es bueno que el hombre esté solo; le haré ayuda idónea para él.",
            en: "It is not good that the man should be alone; I will make him an help meet for him.",
          },
          ref: { es: "Génesis 2:18", en: "Genesis 2:18" },
        },
        {
          type: "p",
          text: {
            es: "Dios creó la familia como parte de la bendición del Reino. Había compañerismo, unidad, complemento y multiplicación.",
            en: "God created the family as part of the Kingdom's blessing. There was companionship, unity, completion and multiplication.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Fructificad y multiplicaos; llenad la tierra…",
            en: "Be fruitful, and multiply, and replenish the earth…",
          },
          ref: { es: "Génesis 1:28", en: "Genesis 1:28" },
        },
        {
          type: "note",
          text: {
            es: "El Reino de Dios no fue diseñado para la soledad, sino para la familia.",
            en: "The Kingdom of God was not designed for loneliness, but for family.",
          },
        },
      ],
    },

    // ---------- Capítulo 5 ----------
    {
      id: "capitulo-5",
      label: { es: "Capítulo 5", en: "Chapter 5" },
      title: { es: "La estrategia de la serpiente", en: "The serpent's strategy" },
      blocks: [
        {
          type: "p",
          text: {
            es: "La misión principal de Satanás es sacar al hombre del estado de deleite y del gobierno de Dios.",
            en: "Satan's main mission is to pull man out of the state of delight and out from under God's rule.",
          },
        },
        { type: "p", text: { es: "La serpiente usó mentiras.", en: "The serpent used lies." } },
        { type: "p", text: { es: "Primero sembró duda sobre la palabra de Dios:", en: "First he sowed doubt about God's word:" } },
        {
          type: "quote",
          text: { es: "¿Conque Dios os ha dicho…?", en: "Yea, hath God said…?" },
          ref: { es: "Génesis 3:1", en: "Genesis 3:1" },
        },
        { type: "p", text: { es: "Después negó las consecuencias del pecado:", en: "Then he denied the consequences of sin:" } },
        {
          type: "quote",
          text: { es: "No moriréis.", en: "Ye shall not surely die." },
          ref: { es: "Génesis 3:4", en: "Genesis 3:4" },
        },
        { type: "p", text: { es: "Luego promovió la autosuficiencia:", en: "Then he promoted self-sufficiency:" } },
        {
          type: "quote",
          text: { es: "Seréis como Dios.", en: "Ye shall be as gods." },
          ref: { es: "Génesis 3:5", en: "Genesis 3:5" },
        },
        {
          type: "p",
          text: {
            es: "Esta es una raíz del humanismo. El mensaje es: no necesitas depender de Dios; tú puedes ocupar su lugar.",
            en: "This is a root of humanism. The message is: you don't need to depend on God; you can take His place.",
          },
        },
        {
          type: "p",
          text: {
            es: "Adán debía gobernar la tierra desde el Edén y extender ese gobierno, pero al desobedecer entregó autoridad.",
            en: "Adam was to govern the earth from Eden and extend that rule, but by disobeying he handed over authority.",
          },
        },
        { type: "p", text: { es: "Por eso Satanás le dijo a Jesús:", en: "That is why Satan told Jesus:" } },
        {
          type: "quote",
          text: {
            es: "A mí me ha sido entregada, y a quien quiero la doy.",
            en: "That is delivered unto me; and to whomsoever I will I give it.",
          },
          ref: { es: "Lucas 4:6", en: "Luke 4:6" },
        },
        {
          type: "p",
          text: {
            es: "Satanás le ofreció a Jesús los reinos sin la cruz. Le estaba diciendo: obtén el gobierno sin obediencia, sin sacrificio y sin pagar el precio.",
            en: "Satan offered Jesus the kingdoms without the cross. He was saying: get the rule without obedience, without sacrifice, without paying the price.",
          },
        },
        {
          type: "p",
          text: {
            es: "Pero Jesús sabía que el Reino no se recupera negociando con Satanás, sino venciéndolo.",
            en: "But Jesus knew that the Kingdom is not recovered by negotiating with Satan, but by defeating him.",
          },
        },
      ],
    },

    // ---------- Capítulo 6 ----------
    {
      id: "capitulo-6",
      label: { es: "Capítulo 6", en: "Chapter 6" },
      title: { es: "Las consecuencias de salir del Edén", en: "The consequences of leaving Eden" },
      blocks: [
        { type: "p", text: { es: "Al salir del Edén, el hombre perdió cobertura.", en: "When man left Eden, he lost his covering." } },
        {
          type: "quote",
          text: { es: "Y conocieron que estaban desnudos.", en: "And they knew that they were naked." },
          ref: { es: "Génesis 3:7", en: "Genesis 3:7" },
        },
        { type: "p", text: { es: "También la tierra fue maldecida.", en: "The ground was also cursed." } },
        {
          type: "quote",
          text: { es: "Maldita será la tierra por tu causa…", en: "Cursed is the ground for thy sake…" },
          ref: { es: "Génesis 3:17", en: "Genesis 3:17" },
        },
        { type: "p", text: { es: "El deleite se convirtió en esfuerzo.", en: "Delight turned into toil." } },
        {
          type: "quote",
          text: { es: "Espinos y cardos te producirá…", en: "Thorns also and thistles shall it bring forth to thee…" },
          ref: { es: "Génesis 3:18", en: "Genesis 3:18" },
        },
        { type: "p", text: { es: "Ahora habría dolor, sudor, opresión y frustración.", en: "Now there would be pain, sweat, oppression and frustration." } },
      ],
    },

    // ---------- Capítulo 7 ----------
    {
      id: "capitulo-7",
      label: { es: "Capítulo 7", en: "Chapter 7" },
      title: { es: "La restauración del Reino", en: "The restoration of the Kingdom" },
      blocks: [
        { type: "p", text: { es: "Dios no abandonó al hombre. Primero lo cubrió.", en: "God did not abandon man. First He covered him." } },
        {
          type: "quote",
          text: {
            es: "Y Jehová Dios hizo al hombre y a su mujer túnicas de pieles, y los vistió.",
            en: "Unto Adam also and to his wife did the LORD God make coats of skins, and clothed them.",
          },
          ref: { es: "Génesis 3:21", en: "Genesis 3:21" },
        },
        { type: "p", text: { es: "La cobertura habla de redención.", en: "The covering speaks of redemption." } },
        {
          type: "p",
          text: {
            es: "El primer Adán perdió el Reino. El segundo Adán, Cristo, recuperó el Reino.",
            en: "The first Adam lost the Kingdom. The second Adam, Christ, recovered the Kingdom.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Porque así como en Adán todos mueren, también en Cristo todos serán vivificados.",
            en: "For as in Adam all die, even so in Christ shall all be made alive.",
          },
          ref: { es: "1 Corintios 15:22", en: "1 Corinthians 15:22" },
        },
        {
          type: "p",
          text: {
            es: "Cristo vino a restaurar lo que Adán perdió. Nuestra labor ahora es volver a establecer el Reino de Dios en los territorios que la serpiente ha ocupado.",
            en: "Christ came to restore what Adam lost. Our work now is to re-establish the Kingdom of God in the territories the serpent has occupied.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Todo lugar que pisare la planta de vuestro pie, os lo he entregado.",
            en: "Every place that the sole of your foot shall tread upon, that have I given unto you.",
          },
          ref: { es: "Josué 1:3", en: "Joshua 1:3" },
        },
        { type: "p", text: { es: "La tierra prometida también es una figura del Edén:", en: "The promised land is also a figure of Eden:" } },
        {
          type: "quote",
          text: { es: "Una tierra que fluye leche y miel.", en: "A land flowing with milk and honey." },
          ref: { es: "Éxodo 3:8", en: "Exodus 3:8" },
        },
        {
          type: "highlight",
          text: {
            es: "Frase central: La guerra espiritual consiste en restaurar el Reino de Dios en las áreas donde la serpiente robó el deleite, para volver a establecer la presencia, el propósito, la familia y el gobierno de Dios sobre la tierra.",
            en: "Key thought: Spiritual warfare is about restoring the Kingdom of God in the areas where the serpent stole the delight, so that God's presence, purpose, family and rule are re-established on the earth.",
          },
        },
      ],
    },

    // ---------- Capítulo 8 ----------
    {
      id: "capitulo-8",
      label: { es: "Capítulo 8", en: "Chapter 8" },
      title: { es: "La oración y la manifestación del Reino", en: "Prayer and the manifestation of the Kingdom" },
      blocks: [
        { type: "p", text: { es: "Texto central:", en: "Core text:" } },
        {
          type: "quote",
          text: {
            es: "Mas si por el dedo de Dios echo yo fuera los demonios, ciertamente el reino de Dios ha llegado a vosotros.",
            en: "But if I with the finger of God cast out devils, no doubt the kingdom of God is come upon you.",
          },
          ref: { es: "Lucas 11:20", en: "Luke 11:20" },
        },
        { type: "p", text: { es: "Antes de hablar de guerra espiritual, Jesús enseña a orar.", en: "Before speaking about spiritual warfare, Jesus teaches how to pray." } },
        {
          type: "quote",
          text: {
            es: "Aconteció que estaba Jesús orando en un lugar…",
            en: "And it came to pass, that, as he was praying in a certain place…",
          },
          ref: { es: "Lucas 11:1", en: "Luke 11:1" },
        },
        {
          type: "p",
          text: {
            es: "La oración atrae la presencia de Dios y provoca que el cielo invada la tierra. La liberación no comienza con gritos, comienza con comunión.",
            en: "Prayer draws the presence of God and causes heaven to invade earth. Deliverance does not begin with shouting; it begins with communion.",
          },
        },
        {
          type: "p",
          text: {
            es: "Primero Jesús ora, después enseña a orar, y luego vemos la manifestación del Reino echando fuera demonios.",
            en: "First Jesus prays, then He teaches how to pray, and then we see the manifestation of the Kingdom casting out demons.",
          },
        },
        { type: "h3", text: { es: "La oración del Reino", en: "The prayer of the Kingdom" } },
        {
          type: "p",
          text: {
            es: "Cuando decimos “Padre nuestro”, reconocemos paternidad. El Reino comienza con hijos que reconocen a Dios como Padre.",
            en: "When we say \"Our Father\", we recognize fatherhood. The Kingdom begins with children who recognize God as Father.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Porque el anhelo ardiente de la creación es el aguardar la manifestación de los hijos de Dios.",
            en: "For the earnest expectation of the creature waiteth for the manifestation of the sons of God.",
          },
          ref: { es: "Romanos 8:19", en: "Romans 8:19" },
        },
        {
          type: "p",
          text: {
            es: "“Santificado sea tu nombre” habla de adoración. Donde hay adoración verdadera, la idolatría pierde autoridad.",
            en: "\"Hallowed be thy name\" speaks of worship. Where there is true worship, idolatry loses its authority.",
          },
        },
        {
          type: "p",
          text: {
            es: "“Venga tu Reino” significa que el gobierno de Dios venga a la tierra.",
            en: "\"Thy Kingdom come\" means that God's rule comes to the earth.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Venga tu reino. Hágase tu voluntad, como en el cielo, así también en la tierra.",
            en: "Thy kingdom come. Thy will be done, as in heaven, so in earth.",
          },
          ref: { es: "Lucas 11:2 / Mateo 6:10", en: "Luke 11:2 / Matthew 6:10" },
        },
        {
          type: "p",
          text: {
            es: "“El pan nuestro de cada día” muestra que en el Reino hay provisión.",
            en: "\"Give us this day our daily bread\" shows that in the Kingdom there is provision.",
          },
        },
        {
          type: "p",
          text: {
            es: "“Perdona nuestros pecados” enseña que el Reino se mueve en un ambiente de perdón.",
            en: "\"Forgive us our sins\" teaches that the Kingdom moves in an atmosphere of forgiveness.",
          },
        },
        {
          type: "p",
          text: {
            es: "“No nos metas en tentación” significa: guárdanos de caer. La tentación busca sacarnos del gobierno de Dios.",
            en: "\"Lead us not into temptation\" means: keep us from falling. Temptation seeks to pull us out from under God's rule.",
          },
        },
        {
          type: "p",
          text: {
            es: "“Líbranos del mal” muestra que la oración del Reino incluye guerra espiritual.",
            en: "\"Deliver us from evil\" shows that the prayer of the Kingdom includes spiritual warfare.",
          },
        },
        {
          type: "quote",
          text: { es: "Mas líbranos del mal.", en: "But deliver us from evil." },
          ref: { es: "Mateo 6:13", en: "Matthew 6:13" },
        },
        { type: "h3", text: { es: "Pedir, buscar y llamar", en: "Ask, seek and knock" } },
        {
          type: "quote",
          text: {
            es: "Pedid, y se os dará; buscad, y hallaréis; llamad, y se os abrirá.",
            en: "Ask, and it shall be given you; seek, and ye shall find; knock, and it shall be opened unto you.",
          },
          ref: { es: "Lucas 11:9", en: "Luke 11:9" },
        },
        { type: "p", text: { es: "El Reino se manifiesta en los que insisten.", en: "The Kingdom manifests in those who persist." } },
        {
          type: "quote",
          text: {
            es: "Desde los días de Juan el Bautista hasta ahora, el reino de los cielos sufre violencia, y los violentos lo arrebatan.",
            en: "And from the days of John the Baptist until now the kingdom of heaven suffereth violence, and the violent take it by force.",
          },
          ref: { es: "Mateo 11:12", en: "Matthew 11:12" },
        },
        { type: "p", text: { es: "Lo primero que debemos pedir es el Espíritu Santo.", en: "The first thing we must ask for is the Holy Spirit." } },
        {
          type: "quote",
          text: {
            es: "Pues si vosotros, siendo malos, sabéis dar buenas dádivas a vuestros hijos, ¿cuánto más vuestro Padre celestial dará el Espíritu Santo a los que se lo pidan?",
            en: "If ye then, being evil, know how to give good gifts unto your children: how much more shall your heavenly Father give the Holy Spirit to them that ask him?",
          },
          ref: { es: "Lucas 11:13", en: "Luke 11:13" },
        },
        { type: "p", text: { es: "El Reino no se establece por fuerza humana.", en: "The Kingdom is not established by human strength." } },
        {
          type: "quote",
          text: {
            es: "No con ejército, ni con fuerza, sino con mi Espíritu, ha dicho Jehová de los ejércitos.",
            en: "Not by might, nor by power, but by my spirit, saith the LORD of hosts.",
          },
          ref: { es: "Zacarías 4:6", en: "Zechariah 4:6" },
        },
      ],
    },

    // ---------- Capítulo 9 ----------
    {
      id: "capitulo-9",
      label: { es: "Capítulo 9", en: "Chapter 9" },
      title: { es: "Jesús echa fuera demonios y el Reino llega", en: "Jesus casts out demons and the Kingdom comes" },
      blocks: [
        {
          type: "quote",
          text: {
            es: "Estaba Jesús echando fuera un demonio, que era mudo…",
            en: "And he was casting out a devil, and it was dumb…",
          },
          ref: { es: "Lucas 11:14", en: "Luke 11:14" },
        },
        {
          type: "p",
          text: {
            es: "La liberación es una señal de que el Reino de Dios llegó. Donde Jesús llega, los demonios no permanecen cómodos.",
            en: "Deliverance is a sign that the Kingdom of God has arrived. Where Jesus arrives, demons do not stay comfortable.",
          },
        },
        { type: "p", text: { es: "Algunos acusaron a Jesús diciendo:", en: "Some accused Jesus, saying:" } },
        {
          type: "quote",
          text: {
            es: "Por Beelzebú, príncipe de los demonios, echa fuera los demonios.",
            en: "He casteth out devils through Beelzebub the chief of the devils.",
          },
          ref: { es: "Lucas 11:15", en: "Luke 11:15" },
        },
        {
          type: "p",
          text: {
            es: "Beelzebú significa comúnmente “señor de las moscas” o “señor de la inmundicia”. En el tiempo de Jesús se usaba como un título asociado a Satanás o al príncipe de los demonios.",
            en: "Beelzebub commonly means \"lord of flies\" or \"lord of filth\". In Jesus' time it was used as a title associated with Satan or the prince of demons.",
          },
        },
        {
          type: "p",
          text: {
            es: "Representa inmundicia, engaño, acusación, gobierno demoníaco, opresión espiritual, corrupción, idolatría y descomposición espiritual.",
            en: "It represents filthiness, deception, accusation, demonic rule, spiritual oppression, corruption, idolatry and spiritual decay.",
          },
        },
        { type: "p", text: { es: "Jesús respondió hablando de reinos:", en: "Jesus answered by speaking of kingdoms:" } },
        {
          type: "quote",
          text: {
            es: "Todo reino dividido contra sí mismo, es asolado; y una casa dividida contra sí misma, cae.",
            en: "Every kingdom divided against itself is brought to desolation; and a house divided against a house falleth.",
          },
          ref: { es: "Lucas 11:17", en: "Luke 11:17" },
        },
        {
          type: "p",
          text: {
            es: "Aquí Jesús revela que hay dos reinos en conflicto: el Reino de Dios y el reino de las tinieblas.",
            en: "Here Jesus reveals that there are two kingdoms in conflict: the Kingdom of God and the kingdom of darkness.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Se levantará nación contra nación, y reino contra reino.",
            en: "For nation shall rise against nation, and kingdom against kingdom.",
          },
          ref: { es: "Mateo 24:7", en: "Matthew 24:7" },
        },
        { type: "p", text: { es: "La guerra espiritual es un conflicto entre gobiernos espirituales.", en: "Spiritual warfare is a conflict between spiritual governments." } },
        { type: "h3", text: { es: "El dedo de Dios", en: "The finger of God" } },
        {
          type: "quote",
          text: {
            es: "Mas si por el dedo de Dios echo yo fuera los demonios, ciertamente el reino de Dios ha llegado a vosotros.",
            en: "But if I with the finger of God cast out devils, no doubt the kingdom of God is come upon you.",
          },
          ref: { es: "Lucas 11:20", en: "Luke 11:20" },
        },
        { type: "p", text: { es: "Mateo dice:", en: "Matthew says:" } },
        {
          type: "quote",
          text: {
            es: "Pero si yo por el Espíritu de Dios echo fuera los demonios, ciertamente ha llegado a vosotros el reino de Dios.",
            en: "But if I cast out devils by the Spirit of God, then the kingdom of God is come unto you.",
          },
          ref: { es: "Mateo 12:28", en: "Matthew 12:28" },
        },
        {
          type: "p",
          text: {
            es: "El dedo de Dios se conecta con la operación directa del poder de Dios por medio del Espíritu Santo.",
            en: "The finger of God is connected with the direct operation of God's power through the Holy Spirit.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Entonces los hechiceros dijeron a Faraón: Dedo de Dios es éste.",
            en: "Then the magicians said unto Pharaoh, This is the finger of God.",
          },
          ref: { es: "Éxodo 8:19", en: "Exodus 8:19" },
        },
        {
          type: "p",
          text: {
            es: "Cuando el dedo de Dios se manifiesta, los poderes de las tinieblas reconocen que hay una autoridad superior.",
            en: "When the finger of God is manifest, the powers of darkness recognize that there is a superior authority.",
          },
        },
        { type: "h3", text: { es: "El hombre fuerte armado", en: "The armed strong man" } },
        {
          type: "quote",
          text: {
            es: "Cuando el hombre fuerte armado guarda su palacio, en paz está lo que posee.",
            en: "When a strong man armed keepeth his palace, his goods are in peace.",
          },
          ref: { es: "Lucas 11:21", en: "Luke 11:21" },
        },
        {
          type: "p",
          text: {
            es: "El hombre fuerte representa una autoridad espiritual demoníaca que guarda una casa, una vida, una región o un territorio.",
            en: "The strong man represents a demonic spiritual authority that keeps a house, a life, a region or a territory.",
          },
        },
        { type: "p", text: { es: "Pero viene uno más fuerte:", en: "But a stronger one comes:" } },
        {
          type: "quote",
          text: {
            es: "Pero cuando viene otro más fuerte que él y le vence, le quita todas sus armas en que confiaba, y reparte el botín.",
            en: "But when a stronger than he shall come upon him, and overcome him, he taketh from him all his armour wherein he trusted, and divideth his spoils.",
          },
          ref: { es: "Lucas 11:22", en: "Luke 11:22" },
        },
        { type: "p", text: { es: "El más fuerte es Cristo obrando por el Espíritu Santo.", en: "The stronger one is Christ working through the Holy Spirit." } },
        { type: "h3", text: { es: "No hay neutralidad", en: "There is no neutrality" } },
        {
          type: "quote",
          text: {
            es: "El que no es conmigo, contra mí es; y el que conmigo no recoge, desparrama.",
            en: "He that is not with me is against me: and he that gathereth not with me scattereth.",
          },
          ref: { es: "Lucas 11:23", en: "Luke 11:23" },
        },
        {
          type: "p",
          text: {
            es: "En guerra espiritual no existe neutralidad. O recogemos con Cristo, o desparramamos.",
            en: "In spiritual warfare there is no neutrality. Either we gather with Christ, or we scatter.",
          },
        },
        {
          type: "highlight",
          text: {
            es: "Frase central: La oración abre la puerta para que el Reino de Dios descienda; el Espíritu Santo manifiesta ese Reino; y la liberación demuestra que el gobierno de Dios ha llegado para desplazar al reino de las tinieblas.",
            en: "Key thought: Prayer opens the door for the Kingdom of God to descend; the Holy Spirit manifests that Kingdom; and deliverance demonstrates that God's rule has arrived to displace the kingdom of darkness.",
          },
        },
      ],
    },

    // ---------- Capítulo 10 ----------
    {
      id: "capitulo-10",
      label: { es: "Capítulo 10", en: "Chapter 10" },
      title: { es: "La casa barrida, adornada y vacía", en: "The swept, adorned and empty house" },
      blocks: [
        {
          type: "p",
          text: {
            es: "Después de hablar del hombre fuerte, Jesús enseña sobre el retorno del espíritu inmundo.",
            en: "After speaking of the strong man, Jesus teaches about the return of the unclean spirit.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Cuando el espíritu inmundo sale del hombre, anda por lugares secos, buscando reposo; y no hallándolo, dice: Volveré a mi casa de donde salí.",
            en: "When the unclean spirit is gone out of a man, he walketh through dry places, seeking rest; and finding none, he saith, I will return unto my house whence I came out.",
          },
          ref: { es: "Lucas 11:24", en: "Luke 11:24" },
        },
        {
          type: "p",
          text: {
            es: "La liberación no termina cuando el espíritu sale; ahí comienza el establecimiento del Reino.",
            en: "Deliverance does not end when the spirit leaves; that is where the establishing of the Kingdom begins.",
          },
        },
        {
          type: "p",
          text: {
            es: "Los lugares secos representan espacios áridos, vacíos y sin vida: corazones sin oración, áreas sin presencia de Dios, lugares sin palabra, ambientes de aridez espiritual y territorios donde no fluye el Espíritu.",
            en: "Dry places represent arid, empty and lifeless spaces: hearts without prayer, areas without God's presence, places without the Word, atmospheres of spiritual dryness and territories where the Spirit does not flow.",
          },
        },
        {
          type: "p",
          text: {
            es: "El espíritu intenta regresar al lugar de donde fue expulsado. Esto enseña que la guerra espiritual es permanente.",
            en: "The spirit tries to return to the place from which it was cast out. This teaches that spiritual warfare is ongoing.",
          },
        },
        {
          type: "quote",
          text: { es: "Y cuando llega, la halla barrida y adornada.", en: "And when he cometh, he findeth it swept and garnished." },
          ref: { es: "Lucas 11:25", en: "Luke 11:25" },
        },
        { type: "p", text: { es: "Mateo añade:", en: "Matthew adds:" } },
        {
          type: "quote",
          text: {
            es: "Y cuando llega, la halla desocupada, barrida y adornada.",
            en: "And when he is come, he findeth it empty, swept, and garnished.",
          },
          ref: { es: "Mateo 12:44", en: "Matthew 12:44" },
        },
        { type: "p", text: { es: "La casa estaba limpia, pero desocupada.", en: "The house was clean, but empty." } },
        {
          type: "p",
          text: {
            es: "La liberación saca lo inmundo, pero la vida debe llenarse con el Espíritu Santo, la Palabra de Dios, oración, adoración, obediencia, discipulado y comunión con Dios.",
            en: "Deliverance removes what is unclean, but life must be filled with the Holy Spirit, the Word of God, prayer, worship, obedience, discipleship, and fellowship with God.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Entonces va, y toma otros siete espíritus peores que él; y entrados, moran allí; y el postrer estado de aquel hombre viene a ser peor que el primero.",
            en: "Then goeth he, and taketh with himself seven other spirits more wicked than himself; and they enter in, and dwell there: and the last state of that man is worse than the first.",
          },
          ref: { es: "Lucas 11:26", en: "Luke 11:26" },
        },
        {
          type: "p",
          text: {
            es: "El peor estado no viene por haber sido libre, sino por haber quedado vacío después de ser libre.",
            en: "The worse state does not come from having been set free, but from having remained empty after being set free.",
          },
        },
        { type: "h3", text: { es: "Desocupar y ocupar", en: "Empty out and occupy" } },
        { type: "p", text: { es: "La guerra espiritual tiene dos movimientos.", en: "Spiritual warfare has two movements." } },
        {
          type: "p",
          text: {
            es: "Primero, desocupar: sacar demonios, ataduras, mentiras, pecado, heridas, pactos, maldiciones y viejas estructuras.",
            en: "First, empty out: remove demons, bondages, lies, sin, wounds, pacts, curses and old structures.",
          },
        },
        {
          type: "p",
          text: {
            es: "Segundo, ocupar: establecer la presencia de Dios, la Palabra, el Espíritu Santo, los frutos del Espíritu, los dones del Espíritu, la obediencia y la identidad de hijo.",
            en: "Second, occupy: establish God's presence, the Word, the Holy Spirit, the fruit of the Spirit, the gifts of the Spirit, obedience and the identity of a son.",
          },
        },
        { type: "p", text: { es: "La casa liberada debe convertirse en habitación de Dios.", en: "The delivered house must become God's dwelling place." } },
        {
          type: "quote",
          text: {
            es: "La palabra de Cristo more en abundancia en vosotros…",
            en: "Let the word of Christ dwell in you richly…",
          },
          ref: { es: "Colosenses 3:16", en: "Colossians 3:16" },
        },
        {
          type: "quote",
          text: { es: "Sed llenos del Espíritu.", en: "Be filled with the Spirit." },
          ref: { es: "Efesios 5:18", en: "Ephesians 5:18" },
        },
        {
          type: "quote",
          text: {
            es: "Habite Cristo por la fe en vuestros corazones…",
            en: "That Christ may dwell in your hearts by faith…",
          },
          ref: { es: "Efesios 3:17", en: "Ephesians 3:17" },
        },
        {
          type: "highlight",
          text: {
            es: "Frase central: La liberación limpia la casa, pero el Reino la ocupa. Si el corazón queda vacío, el enemigo intenta volver; pero si Cristo gobierna, la casa queda llena de la presencia, la palabra y el Espíritu de Dios.",
            en: "Key thought: Deliverance cleans the house, but the Kingdom occupies it. If the heart is left empty, the enemy tries to return; but if Christ reigns, the house remains full of the presence, the Word, and the Spirit of God.",
          },
        },
      ],
    },

    // ---------- Capítulo 11 ----------
    {
      id: "capitulo-11",
      label: { es: "Capítulo 11", en: "Chapter 11" },
      title: { es: "Arrancar, derribar, edificar y plantar", en: "To pluck up, break down, build and plant" },
      blocks: [
        { type: "p", text: { es: "La liberación no es solamente arrancar; es volver a construir el Reino de Dios.", en: "Deliverance is not only about pulling up; it is about rebuilding the Kingdom of God." } },
        {
          type: "quote",
          text: {
            es: "Mira que te he puesto en este día sobre naciones y sobre reinos, para arrancar y para destruir, para arruinar y para derribar, para edificar y para plantar.",
            en: "See, I have this day set thee over the nations and over the kingdoms, to root out, and to pull down, and to destroy, and to throw down, to build, and to plant.",
          },
          ref: { es: "Jeremías 1:10", en: "Jeremiah 1:10" },
        },
        { type: "p", text: { es: "Dios divide la obra en dos partes.", en: "God divides the work into two parts." } },
        { type: "h3", text: { es: "Primera etapa: desocupar el territorio", en: "First stage: emptying out the territory" } },
        {
          type: "p",
          text: {
            es: "Arrancar significa quitar de raíz. Se arrancan mentiras, pecados, idolatrías, pactos incorrectos, ataduras y fortalezas mentales.",
            en: "To root out means to remove from the root. Lies, sins, idolatries, wrong pacts, bondages and mental strongholds are pulled up.",
          },
        },
        {
          type: "p",
          text: {
            es: "Destruir significa quitar estructuras espirituales levantadas contra el conocimiento de Dios.",
            en: "To destroy means to remove spiritual structures raised against the knowledge of God.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Derribando argumentos y toda altivez que se levanta contra el conocimiento de Dios.",
            en: "Casting down imaginations, and every high thing that exalteth itself against the knowledge of God.",
          },
          ref: { es: "2 Corintios 10:5", en: "2 Corinthians 10:5" },
        },
        { type: "p", text: { es: "Arruinar es desmantelar aquello que el enemigo había construido.", en: "To ruin is to dismantle what the enemy had built." } },
        {
          type: "p",
          text: {
            es: "Derribar es quitar aquello que usurpó el lugar de Dios: altares falsos, orgullo, temores, dependencias y gobiernos espirituales incorrectos.",
            en: "To throw down is to remove what usurped God's place: false altars, pride, fears, dependencies and wrong spiritual governments.",
          },
        },
        { type: "h3", text: { es: "Segunda etapa: establecer el Reino", en: "Second stage: establishing the Kingdom" } },
        {
          type: "p",
          text: {
            es: "Muchos creyentes sacan, pero no establecen. Por eso Jesús dijo que la casa estaba desocupada, barrida y adornada.",
            en: "Many believers cast out, but do not establish. That is why Jesus said the house was empty, swept and garnished.",
          },
        },
        { type: "p", text: { es: "Edificar es construir nuevamente. Dios no deja ruinas; Dios restaura.", en: "To build is to construct again. God does not leave ruins; God restores." } },
        {
          type: "quote",
          text: {
            es: "Y reedificarán las ruinas antiguas, y levantarán los asolamientos primeros…",
            en: "And they shall build the old wastes, they shall raise up the former desolations…",
          },
          ref: { es: "Isaías 61:4", en: "Isaiah 61:4" },
        },
        {
          type: "p",
          text: {
            es: "Debemos edificar la oración, la identidad de hijo, la obediencia, los hábitos espirituales y el carácter.",
            en: "We must build up prayer, the identity of a son, obedience, spiritual habits and character.",
          },
        },
        {
          type: "p",
          text: {
            es: "Restaurar los muros representa protección. Nehemías restauró puertas, muros, orden y gobierno.",
            en: "Restoring the walls represents protection. Nehemiah restored gates, walls, order and government.",
          },
        },
        {
          type: "p",
          text: {
            es: "Plantar significa sembrar algo nuevo: la Palabra de Dios, la presencia del Espíritu Santo, los frutos del Espíritu y los dones espirituales.",
            en: "To plant means to sow something new: the Word of God, the presence of the Holy Spirit, the fruit of the Spirit and the spiritual gifts.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Mas el fruto del Espíritu es amor, gozo, paz…",
            en: "But the fruit of the Spirit is love, joy, peace…",
          },
          ref: { es: "Gálatas 5:22-23", en: "Galatians 5:22-23" },
        },
        {
          type: "quote",
          text: {
            es: "Pero a cada uno le es dada la manifestación del Espíritu para provecho.",
            en: "But the manifestation of the Spirit is given to every man to profit withal.",
          },
          ref: { es: "1 Corintios 12:7", en: "1 Corinthians 12:7" },
        },
        { type: "p", text: { es: "El propósito final del Reino es restaurar.", en: "The final purpose of the Kingdom is to restore." } },
        {
          type: "quote",
          text: {
            es: "…hasta los tiempos de la restauración de todas las cosas…",
            en: "…until the times of restitution of all things…",
          },
          ref: { es: "Hechos 3:21", en: "Acts 3:21" },
        },
        {
          type: "p",
          text: {
            es: "Cristo vino a restaurar la relación con Dios, la identidad, la familia, el propósito, el gobierno y la tierra que fue perdida.",
            en: "Christ came to restore the relationship with God, identity, family, purpose, government and the land that was lost.",
          },
        },
        { type: "h3", text: { es: "Aplicación práctica después de la liberación", en: "Practical application after deliverance" } },
        {
          type: "p",
          text: {
            es: "Después de arrancar y derribar debemos llenar la casa con la Palabra, desarrollar una vida de oración, buscar la llenura del Espíritu Santo, restaurar relaciones sanas, establecer hábitos espirituales, reconstruir los muros de protección y plantar nuevos pensamientos y nuevas conductas.",
            en: "After rooting out and throwing down we must fill the house with the Word, develop a life of prayer, seek the fullness of the Holy Spirit, restore healthy relationships, establish spiritual habits, rebuild the walls of protection and plant new thoughts and new behaviors.",
          },
        },
        {
          type: "highlight",
          text: {
            es: "Frase central: La guerra espiritual no consiste solamente en expulsar las tinieblas, sino en restaurar el Reino de Dios. Primero arrancamos y derribamos; después edificamos, restauramos, sembramos y plantamos para que Cristo gobierne permanentemente la casa.",
            en: "Key thought: Spiritual warfare is not only about expelling darkness, but about restoring the Kingdom of God. First we root out and throw down; then we build, restore, sow and plant so that Christ may permanently reign over the house.",
          },
        },
      ],
    },

    // ---------- Capítulo 12 ----------
    {
      id: "capitulo-12",
      label: { es: "Capítulo 12", en: "Chapter 12" },
      title: { es: "De la tempestad a la transformación de una región", en: "From the storm to the transformation of a region" },
      blocks: [
        { type: "p", text: { es: "Texto base: Marcos 4:35-41 y Marcos 5:1-20.", en: "Text: Mark 4:35-41 and Mark 5:1-20." } },
        { type: "p", text: { es: "Jesús dijo:", en: "Jesus said:" } },
        {
          type: "quote",
          text: { es: "Pasemos al otro lado.", en: "Let us pass over unto the other side." },
          ref: { es: "Marcos 4:35", en: "Mark 4:35" },
        },
        {
          type: "p",
          text: {
            es: "No los estaba moviendo solamente de un lugar geográfico a otro, sino a una transición espiritual: del temor a la fe, de lo conocido a lo desconocido, del territorio judío al territorio gentil y de ser observadores a ser participantes de la guerra espiritual.",
            en: "He was not only moving them from one geographic place to another, but into a spiritual transition: from fear to faith, from the known to the unknown, from Jewish territory to Gentile territory, and from being observers to being participants in spiritual warfare.",
          },
        },
        { type: "p", text: { es: "La barca representa el llamado, el ministerio, la asignación y la misión.", en: "The boat represents the call, the ministry, the assignment and the mission." } },
        {
          type: "quote",
          text: {
            es: "Y yendo, predicad, diciendo: El reino de los cielos se ha acercado. Sanad enfermos, limpiad leprosos, resucitad muertos, echad fuera demonios…",
            en: "And as ye go, preach, saying, The kingdom of heaven is at hand. Heal the sick, cleanse the lepers, raise the dead, cast out devils…",
          },
          ref: { es: "Mateo 10:7-8", en: "Matthew 10:7-8" },
        },
        { type: "p", text: { es: "Toda asignación encuentra resistencia.", en: "Every assignment meets resistance." } },
        {
          type: "quote",
          text: { es: "Pero se levantó una gran tempestad de viento…", en: "And there arose a great storm of wind…" },
          ref: { es: "Marcos 4:37", en: "Mark 4:37" },
        },
        {
          type: "p",
          text: {
            es: "Muchas veces la batalla más fuerte aparece antes de llegar al territorio que Dios quiere transformar.",
            en: "Many times the fiercest battle appears just before arriving at the territory God wants to transform.",
          },
        },
        { type: "p", text: { es: "Jesús reprendió el viento:", en: "Jesus rebuked the wind:" } },
        {
          type: "quote",
          text: { es: "Calla, enmudece.", en: "Peace, be still." },
          ref: { es: "Marcos 4:39", en: "Mark 4:39" },
        },
        { type: "p", text: { es: "Primero calma la tempestad. Después confronta a la legión.", en: "First He calms the storm. Then He confronts the legion." } },
        { type: "p", text: { es: "Jesús también confrontó el temor:", en: "Jesus also confronted fear:" } },
        {
          type: "quote",
          text: {
            es: "¿Por qué estáis así amedrentados? ¿Cómo no tenéis fe?",
            en: "Why are ye so fearful? how is it that ye have no faith?",
          },
          ref: { es: "Marcos 4:40", en: "Mark 4:40" },
        },
        { type: "p", text: { es: "El temor paraliza; la fe avanza.", en: "Fear paralyzes; faith advances." } },
        { type: "h3", text: { es: "La región de los gadarenos", en: "The region of the Gadarenes" } },
        {
          type: "quote",
          text: {
            es: "Vinieron al otro lado del mar, a la región de los gadarenos.",
            en: "And they came over unto the other side of the sea, into the country of the Gadarenes.",
          },
          ref: { es: "Marcos 5:1", en: "Mark 5:1" },
        },
        {
          type: "p",
          text: {
            es: "La región de los gadarenos, gerasenos o gergesenos formaba parte de la Decápolis.",
            en: "The region of the Gadarenes, Gerasenes or Gergesenes was part of the Decapolis.",
          },
        },
        {
          type: "p",
          text: {
            es: "Decápolis significa “diez ciudades”. Era una federación de ciudades de cultura griega y romana, predominantemente gentil.",
            en: "Decapolis means \"ten cities\". It was a federation of Greek and Roman cultural cities, predominantly Gentile.",
          },
        },
        {
          type: "p",
          text: {
            es: "Las diez ciudades tradicionalmente asociadas son: Damasco, Filadelfia, Gerasa, Gadara, Hipos, Escitópolis, Pela, Dion, Canata y Rafana.",
            en: "The ten cities traditionally associated are: Damascus, Philadelphia, Gerasa, Gadara, Hippos, Scythopolis, Pella, Dion, Canatha and Raphana.",
          },
        },
        {
          type: "p",
          text: {
            es: "Era un territorio lleno de influencia pagana: templos paganos, influencia romana, idolatría, cementerios, actividad comercial y grandes piaras de cerdos.",
            en: "It was a territory full of pagan influence: pagan temples, Roman influence, idolatry, cemeteries, commerce and large herds of swine.",
          },
        },
        { type: "p", text: { es: "Jesús venía de Capernaúm.", en: "Jesus was coming from Capernaum." } },
        {
          type: "quote",
          text: {
            es: "El pueblo asentado en tinieblas vio gran luz.",
            en: "The people which sat in darkness saw great light.",
          },
          ref: { es: "Mateo 4:16", en: "Matthew 4:16" },
        },
        {
          type: "p",
          text: {
            es: "La luz no fue creada para quedarse en un solo territorio; fue creada para invadir las tinieblas.",
            en: "Light was not created to stay in one territory; it was created to invade the darkness.",
          },
        },
        { type: "h3", text: { es: "El hombre fuerte de la región", en: "The strong man of the region" } },
        {
          type: "quote",
          text: {
            es: "Y cuando salió él de la barca, en seguida vino a su encuentro, de los sepulcros, un hombre con un espíritu inmundo.",
            en: "And when he was come out of the ship, immediately there met him out of the tombs a man with an unclean spirit.",
          },
          ref: { es: "Marcos 5:2", en: "Mark 5:2" },
        },
        {
          type: "p",
          text: {
            es: "Este hombre vivía entre los muertos, en aislamiento, bajo opresión, sin control y sin paz.",
            en: "This man lived among the dead, in isolation, under oppression, without control and without peace.",
          },
        },
        {
          type: "quote",
          text: { es: "Nadie le podía atar, ni aun con cadenas.", en: "No man could bind him, no, not with chains." },
          ref: { es: "Marcos 5:3", en: "Mark 5:3" },
        },
        {
          type: "p",
          text: {
            es: "Representa el hombre fuerte que estaba operando en aquella región.",
            en: "He represents the strong man that was operating in that region.",
          },
        },
        { type: "h3", text: { es: "La legión", en: "The legion" } },
        {
          type: "quote",
          text: { es: "Legión me llamo, porque somos muchos.", en: "My name is Legion: for we are many." },
          ref: { es: "Marcos 5:9", en: "Mark 5:9" },
        },
        {
          type: "p",
          text: {
            es: "Una legión romana estaba compuesta aproximadamente por entre 5,000 y 6,000 soldados. Aquí la palabra no necesariamente indica un número exacto de demonios, sino una gran cantidad organizada.",
            en: "A Roman legion was made up of roughly 5,000 to 6,000 soldiers. Here the word does not necessarily indicate an exact number of demons, but a large, organized force.",
          },
        },
        {
          type: "p",
          text: {
            es: "Las tinieblas también operan con estructuras, organización y jerarquías.",
            en: "Darkness also operates with structures, organization and hierarchies.",
          },
        },
        {
          type: "quote",
          text: {
            es: "Y le rogaba mucho que no los enviase fuera de aquella región.",
            en: "And he besought him much that he would not send them away out of the country.",
          },
          ref: { es: "Marcos 5:10", en: "Mark 5:10" },
        },
        {
          type: "p",
          text: {
            es: "Hay influencias espirituales que buscan permanecer en determinados territorios, como el príncipe de Persia y el príncipe de Grecia en Daniel 10:13 y Daniel 10:20.",
            en: "There are spiritual influences that seek to remain in specific territories, like the prince of Persia and the prince of Greece in Daniel 10:13 and Daniel 10:20.",
          },
        },
        { type: "h3", text: { es: "Los cerdos", en: "The swine" } },
        {
          type: "quote",
          text: { es: "Había allí una gran piara de cerdos…", en: "Now there was there nigh unto the mountains a great herd of swine…" },
          ref: { es: "Marcos 5:11", en: "Mark 5:11" },
        },
        {
          type: "p",
          text: {
            es: "Había cerdos porque no era territorio judío, sino gentil. Probablemente era una actividad económica ligada a la cultura griega y al abastecimiento de las poblaciones romanas.",
            en: "There were swine because it was not Jewish but Gentile territory. It was probably an economic activity linked to Greek culture and to supplying Roman populations.",
          },
        },
        {
          type: "p",
          text: {
            es: "Jesús estaba entrando en un sistema completamente opuesto al Reino de Dios.",
            en: "Jesus was entering a system completely opposed to the Kingdom of God.",
          },
        },
        { type: "h3", text: { es: "Justicia, paz y gozo manifestados", en: "Righteousness, peace and joy made manifest" } },
        { type: "p", text: { es: "Después de la liberación ocurrió algo impresionante:", en: "After the deliverance, something remarkable happened:" } },
        {
          type: "quote",
          text: {
            es: "Le vieron sentado, vestido y en su juicio cabal.",
            en: "They see him that was possessed with the devil… sitting, and clothed, and in his right mind.",
          },
          ref: { es: "Marcos 5:15", en: "Mark 5:15" },
        },
        { type: "p", text: { es: "Ahí vemos Romanos 14:17 manifestado.", en: "There we see Romans 14:17 made manifest." } },
        { type: "p", text: { es: "Justicia: Cristo restableció el orden.", en: "Righteousness: Christ re-established order." } },
        { type: "p", text: { es: "Paz: su mente fue restaurada.", en: "Peace: his mind was restored." } },
        { type: "p", text: { es: "Gozo: su vida recuperó propósito.", en: "Joy: his life recovered its purpose." } },
        { type: "h3", text: { es: "Dios encuentra un hombre de paz", en: "God finds a man of peace" } },
        { type: "p", text: { es: "El hombre liberado quiso seguir a Jesús, pero Jesús le dijo:", en: "The delivered man wanted to follow Jesus, but Jesus told him:" } },
        {
          type: "quote",
          text: {
            es: "Vete a tu casa, a los tuyos, y cuéntales cuán grandes cosas el Señor ha hecho contigo.",
            en: "Go home to thy friends, and tell them how great things the Lord hath done for thee.",
          },
          ref: { es: "Marcos 5:19", en: "Mark 5:19" },
        },
        { type: "p", text: { es: "La guerra espiritual comienza en tres niveles:", en: "Spiritual warfare begins on three levels:" } },
        { type: "p", text: { es: "Primero, nosotros mismos. Dios nos libera.", en: "First, ourselves. God sets us free." } },
        { type: "p", text: { es: "Segundo, nuestra casa. Jesús dijo: “Vete a los tuyos.”", en: "Second, our household. Jesus said: \"Go to thy friends.\"" } },
        { type: "p", text: { es: "Tercero, la región. El testimonio impacta territorios.", en: "Third, the region. Testimony impacts territories." } },
        { type: "h3", text: { es: "El primer misionero de Decápolis", en: "The first missionary of Decapolis" } },
        {
          type: "quote",
          text: {
            es: "Y se fue, y comenzó a publicar en Decápolis cuán grandes cosas había hecho Jesús con él; y todos se maravillaban.",
            en: "And he departed, and began to publish in Decapolis how great things Jesus had done for him: and all men did marvel.",
          },
          ref: { es: "Marcos 5:20", en: "Mark 5:20" },
        },
        {
          type: "p",
          text: {
            es: "Jesús transformó a un hombre y ese hombre llevó la gloria de Dios a diez ciudades.",
            en: "Jesus transformed one man and that man carried the glory of God to ten cities.",
          },
        },
        {
          type: "note",
          text: {
            es: "Nota final: Muchas veces Dios no comienza transformando una región entera; comienza transformando a una persona. Porque una persona transformada puede convertirse en un instrumento para que la gloria de Dios alcance toda una región.",
            en: "Final note: Many times God does not start by transforming an entire region; He starts by transforming one person. Because one transformed person can become an instrument for the glory of God to reach a whole region.",
          },
        },
        {
          type: "highlight",
          text: {
            es: "Frase central: La guerra espiritual consiste en llevar la luz de Cristo a territorios dominados por las tinieblas, desarrollando nuestra fe, confrontando la opresión espiritual y estableciendo el Reino de Dios a través de personas transformadas.",
            en: "Key thought: Spiritual warfare consists of carrying the light of Christ into territories ruled by darkness, growing our faith, confronting spiritual oppression, and establishing the Kingdom of God through transformed people.",
          },
        },
      ],
    },

    // ---------- Conclusión ----------
    {
      id: "conclusion",
      label: { es: "Conclusión", en: "Conclusion" },
      title: { es: "Conclusión", en: "Conclusion" },
      blocks: [
        {
          type: "p",
          text: {
            es: "La liberación y el Reino de Dios están profundamente conectados. La liberación no se trata únicamente de expulsar demonios, romper ataduras o limpiar una casa espiritual. La verdadera liberación tiene como propósito final establecer el gobierno de Dios.",
            en: "Deliverance and the Kingdom of God are deeply connected. Deliverance is not only about casting out demons, breaking bondages or cleansing a spiritual house. True deliverance has as its final purpose the establishing of God's rule.",
          },
        },
        {
          type: "p",
          text: {
            es: "Romanos 14:17 resume la evidencia del Reino: Justicia, paz y gozo en el Espíritu Santo.",
            en: "Romans 14:17 sums up the evidence of the Kingdom: Righteousness, peace and joy in the Holy Spirit.",
          },
        },
        { type: "p", text: { es: "La justicia nos alinea con Dios.", en: "Righteousness aligns us with God." } },
        { type: "p", text: { es: "La paz nos reconcilia con Él.", en: "Peace reconciles us with Him." } },
        { type: "p", text: { es: "El gozo confirma que la salvación gobierna nuestro interior.", en: "Joy confirms that salvation reigns within us." } },
        {
          type: "p",
          text: {
            es: "Cristo vino a restaurar lo que Adán perdió. Vino a recuperar territorios, familias, propósitos, identidades y corazones. Por eso, después de ser liberados, no podemos quedar vacíos. La casa debe ser llena de la Palabra, del Espíritu Santo, de oración, adoración, obediencia y comunión con Dios.",
            en: "Christ came to restore what Adam lost. He came to recover territories, families, purposes, identities and hearts. That is why, after being set free, we cannot remain empty. The house must be filled with the Word, the Holy Spirit, prayer, worship, obedience and fellowship with God.",
          },
        },
        {
          type: "p",
          text: {
            es: "La guerra espiritual no termina cuando algo sale. Comienza una nueva etapa cuando Cristo entra a gobernar.",
            en: "Spiritual warfare does not end when something leaves. A new stage begins when Christ enters to rule.",
          },
        },
        {
          type: "highlight",
          text: {
            es: "La liberación limpia la casa, pero el Reino la ocupa. Donde Cristo gobierna, las tinieblas pierden territorio, la presencia de Dios es restaurada y la vida vuelve al diseño original: justicia, paz y gozo en el Espíritu Santo.",
            en: "Deliverance cleans the house, but the Kingdom occupies it. Where Christ reigns, darkness loses territory, God's presence is restored, and life returns to its original design: righteousness, peace and joy in the Holy Spirit.",
          },
        },
      ],
    },
  ],
};

export const BOOKS = {
  [BOOK.id]: BOOK,
};

export default BOOK;
